import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import arrowIcon from "../assets/icons/arrow-down.svg";

interface Props {
  activePage: number;
  totalItems: number;
  totalPages: number;
  limit: number;
  handlePrevClick: () => void;
  handleNextClick: () => void;
  limitsArray?: number[];
  handleLimitClick?: (e: number) => void;
}

const Pagination: React.FC<Props> = ({
  activePage,
  totalItems,
  totalPages,
  handleNextClick,
  handlePrevClick,
  limit,
  limitsArray,
  handleLimitClick,
}) => {
  const { t } = useTranslation();

  const [isOpened, setOpened] = useState<boolean>(false);
  const [isCustomMode, setCustomMode] = useState(false);
  const [customLimit, setCustomLimit] = useState("");
  const selectRef = useRef<HTMLDivElement | null>(null);
  const customInputRef = useRef<HTMLInputElement | null>(null);

  const isCustomLimitValid =
    /^\d+$/.test(customLimit) &&
    Number.isSafeInteger(Number(customLimit)) &&
    Number(customLimit) > 0;

  const closeMenu = () => {
    setOpened(false);
    setCustomMode(false);
  };

  const applyCustomLimit = () => {
    if (!isCustomLimitValid) return;

    handleLimitClick?.(Number(customLimit));
    closeMenu();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpened &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setOpened(false);
        setCustomMode(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpened]);

  useEffect(() => {
    if (isCustomMode) {
      customInputRef.current?.focus();
    }
  }, [isCustomMode]);

  return (
    <div className="pagination-wrap">
      <div className="content">
        <span className="txt">
          {t("showing")} {activePage === 1 ? 1 : limit * (activePage - 1) + 1}-
          {activePage === totalPages ? totalItems : activePage * limit}{" "}
          {t("of")} {totalItems}
        </span>
        {limitsArray && (
          <div
            className={`limits-select ${isOpened ? "opened" : ""}`}
            ref={selectRef}
          >
            <div
              className="select"
              onClick={() => {
                if (isOpened) {
                  closeMenu();
                } else {
                  setOpened(true);
                }
              }}
            >
              <span>
                {limit} / {t("page")}
              </span>
              <img src={arrowIcon} alt="" />
            </div>
            <div className="pagination-menu">
              {isCustomMode ? (
                <>
                  <button
                    className="item pagination-menu__back"
                    type="button"
                    onClick={() => setCustomMode(false)}
                  >
                    &lsaquo; {t("back")}
                  </button>
                  <div className="pagination-menu__custom">
                    <input
                      ref={customInputRef}
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      value={customLimit}
                      placeholder={t("enterNumber")}
                      onChange={(event) => setCustomLimit(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          applyCustomLimit();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="pagination-menu__apply"
                      disabled={!isCustomLimitValid}
                      onClick={applyCustomLimit}
                      aria-label={t("custom")}
                    >
                      ✓
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {limitsArray.map((el) => (
                    <div
                      className="item"
                      key={el}
                      onClick={() => {
                        handleLimitClick?.(el);
                        closeMenu();
                      }}
                    >
                      {el} / {t("page")}
                    </div>
                  ))}
                  <button
                    className="item pagination-menu__custom-trigger"
                    type="button"
                    onClick={() => {
                      setCustomLimit(String(limit));
                      setCustomMode(true);
                    }}
                  >
                    {t("custom")}&hellip;
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="arrows">
        <div
          className={`prev ${activePage === 1 ? "disabled" : ""}`}
          onClick={handlePrevClick}
        >
          <img src={arrowIcon} alt="" />
        </div>
        <div
          className={`next ${activePage === totalPages ? "disabled" : ""}`}
          onClick={handleNextClick}
        >
          <img src={arrowIcon} alt="" />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
